export const designVariants = ['main', 'v1', 'v2', 'v3', 'v4', 'v5'] as const;
export type DesignVariant = (typeof designVariants)[number];

export const designCatalog: Record<DesignVariant, { title: string; description: string }> = {
  main: { title: 'Main interface', description: 'The default Callevate workspace.' },
  v1: {
    title: 'V1 · Guided setup',
    description: 'A familiar campaign-first workspace with clear setup steps.',
  },
  v2: {
    title: 'V2 · Assistant workspace',
    description: 'Find an assistant, manage your team and review activity.',
  },
  v3: {
    title: 'V3 · Agent studio',
    description: 'A compact workspace organized around Build, Deploy and Monitor.',
  },
  v4: {
    title: 'V4 · Voice workspace',
    description: 'A focused monochrome workspace with a compact resource rail.',
  },
  v5: {
    title: 'V5 · Missions workspace',
    description: 'A teal workspace for assistants, campaigns and missions.',
  },
};

export const isDesignVariant = (value: unknown): value is DesignVariant =>
  designVariants.some((variant) => variant === value);

export const designStorageKey = (scope: string) => `callevate:design:v1:${scope}`;
export const readDesignVariant = (scope: string | undefined): DesignVariant => {
  if (!scope) return 'main';
  try {
    const value = sessionStorage.getItem(designStorageKey(scope));
    return isDesignVariant(value) ? value : 'main';
  } catch {
    return 'main';
  }
};

export type WorkspaceModule =
  | 'Agents'
  | 'Ask Callevate'
  | 'Missions'
  | 'Dashboard'
  | 'Jobs'
  | 'Knowledge base'
  | 'Connections'
  | 'Phone numbers'
  | 'Call history'
  | 'Analytics'
  | 'Tools'
  | 'Webhooks'
  | 'Audiences'
  | 'Concurrency'
  | 'SIP trunks'
  | 'WhatsApp'
  | 'Evaluations';

export type DesignNavItem = {
  label: string;
  href?: '/admin/campaign' | '/admin/assistants' | '/billing' | '/admin/access';
  workspace?: WorkspaceModule;
};
export type DesignNavGroup = { label: string; items: DesignNavItem[] };
const campaigns: DesignNavItem = { label: 'Campaigns', href: '/admin/campaign' };
const agents: DesignNavItem = { label: 'Agents', href: '/admin/assistants' };
const workspace = (label: WorkspaceModule): DesignNavItem => ({ label, workspace: label });

// Live entries resolve to existing pages; independent prototype resources never impersonate APIs.
export const variantNavigation: Record<Exclude<DesignVariant, 'main'>, DesignNavGroup[]> = {
  v1: [
    {
      label: 'Workspace',
      items: [campaigns, { ...agents, label: 'Instructions' }, workspace('Call history')],
    },
  ],
  v2: [
    {
      label: 'Assistants',
      items: [
        { ...agents, label: 'Find an assistant' },
        { ...campaigns, label: 'My team' },
      ],
    },
    { label: 'Activity', items: [workspace('Call history')] },
    { label: 'Setup', items: [workspace('Jobs'), workspace('Connections')] },
  ],
  v3: [
    {
      label: 'Build',
      items: [
        { label: 'Agents', workspace: 'Agents' },
        workspace('Jobs'),
        workspace('Knowledge base'),
      ],
    },
    { label: 'Deploy', items: [workspace('Connections'), campaigns, workspace('Phone numbers')] },
    { label: 'Monitor', items: [workspace('Call history'), workspace('Analytics')] },
  ],
  v4: [
    { label: 'Build', items: [{ label: 'Agents', workspace: 'Agents' }, workspace('Jobs')] },
    {
      label: 'Resources',
      items: [
        workspace('Knowledge base'),
        workspace('Concurrency'),
        workspace('Connections'),
        workspace('Webhooks'),
        workspace('Tools'),
      ],
    },
    {
      label: 'Deploy',
      items: [
        workspace('Audiences'),
        campaigns,
        workspace('Phone numbers'),
        workspace('SIP trunks'),
        workspace('WhatsApp'),
      ],
    },
    {
      label: 'Monitor',
      items: [workspace('Evaluations'), workspace('Call history'), workspace('Analytics')],
    },
  ],
  v5: [
    { label: 'Home', items: [workspace('Dashboard')] },
    {
      label: 'Build',
      items: [
        { label: 'Assistants', workspace: 'Agents' },
        workspace('Jobs'),
        workspace('Knowledge base'),
        workspace('Tools'),
      ],
    },
    { label: 'Orchestrate', items: [workspace('Missions'), campaigns, workspace('Audiences')] },
    {
      label: 'Workspace',
      items: [
        workspace('Call history'),
        workspace('Analytics'),
        workspace('Connections'),
        workspace('Phone numbers'),
      ],
    },
  ],
};

export const getVariantNavigation = (
  variant: Exclude<DesignVariant, 'main'>,
  privileges: readonly string[] = [],
): DesignNavGroup[] => [
  ...variantNavigation[variant],
  {
    label: 'Account',
    items: [
      { label: 'Billing', href: '/billing' },
      ...(privileges.some((p) => ['user.manage', 'group.manage'].includes(p))
        ? [{ label: 'Access control', href: '/admin/access' } as DesignNavItem]
        : []),
    ],
  },
];
